import { AGENT_LABEL, type AgentId } from '@tabel/shared/connector/versions';
import { threadsText } from '../../pages/MyAgents/texts';
import { CARD_TEXTS } from './texts-cards';

/*
 * Словарь «Разговора» 006 (specs/006-agent-client/contracts/ui.md §2, §3, §5; «Слова интерфейса» spec.md; FR-038,
 * FR-041). Один словарь для правой панели «Помощник» и страницы «Помощник»: лента, поле сообщения, полосы состояний,
 * список разговоров. Тексты карточек, выбора модели и памяти — `CARD_TEXTS` (`texts-cards.ts`), словарь отдаёт их дальше.
 * Подписи шагов агента и системные строки ленты («Агент остановлен.», «Модель сменилась…») приходят с сервера
 * (`@tabel/shared/agent-events/texts.ts`) — здесь их нет.
 *
 * Язык — ASD-STE100: короткие предложения, одно слово — одно значение, система не говорит «я». Слов «тред», «ход»,
 * «контекст», «вмешательство» нет: разговор, ответ, «Отправить сразу», «Отправить после ответа», «Память разговора».
 * Латиница — только названия агентов («Claude», «Codex», «pi») и команды для терминала (их строит коннектор). Проверяет
 * `texts.test.tsx`.
 */

export { CARD_TEXTS };

/** Заголовки — до 4 слов. */
export const TITLE = {
  panel: 'Помощник',
  page: 'Помощник',
  newThread: 'Новый разговор',
  hello: 'Спросите о правилах расчёта',
  examples: 'Например',
  myThreads: 'Мои разговоры',
  archive: 'Архив',
  agent: 'Агент',
  noThreads: 'Разговоров нет',
  noArchive: 'В архиве пусто',
  notFound: 'Разговора нет',
  unavailable: 'Помощник недоступен',
  listError: 'Разговоры не загрузились',
  dayError: 'Разговор не начат',
} as const;

/** Подписи кнопок, полей и строк — до 6 слов. */
export const LABEL = {
  open: 'Помощник',
  newThread: 'Новый разговор',
  expand: 'Развернуть на страницу «Помощник»',
  toThread: 'К разговору',
  openOnRules: 'Открыть на странице правил',
  rulesPage: 'Правила расчёта',
  publish: 'Опубликовать проект',
  send: 'Отправить',
  sendNow: 'Отправить сразу',
  sendAfter: 'Отправить после ответа',
  stop: 'Остановить агента',
  stopping: 'Остановка…',
  remove: 'Убрать',
  message: 'Сообщение агенту',
  feed: 'Разговор с агентом',
  search: 'Найти разговор',
  rename: 'Переименовать разговор',
  title: 'Название разговора',
  save: 'Сохранить название',
  cancel: 'Отмена',
  archiveThread: 'Убрать разговор в архив',
  restore: 'Вернуть из архива',
  undo: 'Отменить',
  backToList: 'Мои разговоры',
  backFromArchive: 'К разговорам',
  copy: 'Скопировать',
  copied: 'Скопировано',
  command: 'Команда для терминала',
  loading: 'Загрузка…',
  retry: 'Повторить',
  errorCode: 'Код ошибки:',
  chooseAgent: 'Выбрать агента',
  selected: 'выбрано',
  myAgents: 'Открыть «Мои ИИ-клиенты»',
  checking: 'Проверка…',
  checkAgent: 'Проверить агента',
  noRetry: 'Не повторять',
  retryQuestion: 'Повторить вопрос',
  startNew: 'Начать новый разговор',
  reloadModels: 'Загрузить список снова',
  reloadNow: 'Загрузить сейчас',
  askAgent: 'Спросить агента',
  askDay: 'Спросить агента об этом дне',
  starting: 'Агент начинает работу…',
  online: 'в сети',
  offline: 'не в сети',
} as const;

/** Пример в пустом поле: что можно спросить. */
export const PLACEHOLDER = {
  idle: 'Например: почему у дня 7,0 ч вместо 8,0 ч?',
  running: 'Уточнение для агента, например: «и отдельно по ночным сменам»',
  idleShort: 'Например: почему у дня 7,0 ч?',
  runningShort: 'Уточнение для агента',
  search: 'Найти разговор, например: обед',
} as const;

/** Предложения. */
export const T = {
  hello:
    'Агент видит правила, сводки и обезличенные дни. ФИО и номера людей он не получает. Изменения агент готовит в черновике, а публикует человек.',
  agentInNew: 'Другой агент начнёт новый разговор. Этот разговор останется в списке.',
  agentsPage: 'Состояние агентов — на странице «Мои ИИ-клиенты».',
  noComputer: 'Компьютер не подключён к табелю.',
  noComputerFix: 'Подключите его на странице «Мои ИИ-клиенты».',
  offlineWait: 'Агент ответит, когда компьютер подключится.',
  loginFix: 'Войдите в терминале этого компьютера:',
  linkFix: 'Выполните в терминале:',
  rateLimit: 'Модель временно недоступна: исчерпан лимит.',
  memoryWarn: 'Агент может забыть начало разговора.',
  modelsFailed: 'Список моделей не загрузился.',
  modelsFallback: 'Показан запасной список.',
  archived: 'Разговор в архиве.',
  archivedText: 'Писать в него нельзя. Переписка хранится 30 дней.',
  lagging: 'Разговор загружается.',
  laggingText: 'Показаны не все сообщения.',
  readOnly: 'Агент сейчас может только смотреть и объяснять: изменять черновик через него пока нельзя.',
  projectNote: 'Публикует человек. Агент не публикует.',
  noThreads: 'Здесь появятся ваши разговоры с ИИ-агентом.',
  noArchive: 'Здесь появятся разговоры, которые вы уберёте в архив.',
  notFound: 'Такого разговора нет. Начните новый разговор.',
  unavailable: 'Чтобы говорить с агентом, нужно право «ИИ-агент» и компьютер, подключённый к табелю.',
  noMatch: 'Разговоров с такими словами нет.',
  renameFailed: 'Название не сохранено.',
  archivedToast: 'Разговор перенесён в архив',
  restoredToast: 'Разговор возвращён из архива',
  copyFailed: 'Не получилось скопировать. Выделите команду и скопируйте её вручную.',
  stepsFailed: 'Работа агента не загрузилась.',
  sentNow: 'Отправлено сразу: агент прочтёт во время работы.',
  sentAfter: 'Отправлено после ответа.',
  noSteps: 'Агент ответил без обращений к табелю.',
} as const;

/** Подсказки по странице (FR-038): до первого сообщения. ФИО и номеров людей в них нет. */
export const HINTS = {
  rules: [
    'Какие правила расчёта действуют на объекте?',
    'Почему у дня 7,0 ч вместо 8,0 ч?',
    'Что изменится, если сдвинуть начало обеда на 11:00?',
  ],
  timesheet: [
    'Почему у дня 7,0 ч вместо 8,0 ч?',
    'Как считаются ночные часы?',
    'Когда из дня вычитается обед?',
  ],
  day: ['Почему в этот день столько часов?', 'Какие правила сработали в этот день?'],
  assistant: [
    'Какие правила расчёта действуют на объекте?',
    'Почему у дня 7,0 ч вместо 8,0 ч?',
    'Что изменилось в правилах за последний месяц?',
  ],
} as const;

/** Строка «Отправится после ответа: «…» · Убрать»: начало — по причине очереди. */
export const QUEUE_LEAD = {
  turn_running: 'Отправится после ответа:',
  waiting_answer: 'Отправится после ответа:',
  offline: 'Отправится, когда компьютер подключится:',
  busy_computer: 'Отправится, когда компьютер освободится:',
  retry: 'Отправится снова:',
  none: 'Отправится после ответа:',
} as const;

/** Состояние разговора в списке «Мои разговоры». */
export const STATUS_LABEL = {
  running: 'работает',
  waiting: 'ждёт ответа',
  stopping: 'останавливается',
  error: 'ошибка',
} as const;

/** Название агента простым текстом, без логотипа (FR-041). */
export const agentName = (agent: AgentId): string => AGENT_LABEL[agent];

const isOne = (n: number) => n % 10 === 1 && n % 100 !== 11;
const isFew = (n: number) => {
  const d = n % 10;
  const dd = n % 100;
  return d >= 2 && d <= 4 && (dd < 12 || dd > 14);
};
const plural = (n: number, one: string, few: string, many: string) =>
  `${n} ${isOne(n) ? one : isFew(n) ? few : many}`;

/** «20 с», «2 мин», «1 ч 5 мин». */
export function workedText(ms: number): string {
  const s = Math.max(1, Math.round(ms / 1000));
  if (s < 60) return `${s} с`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest > 0 ? `${h} ч ${rest} мин` : `${h} ч`;
}

/** «1 шаг», «2 шага», «8 шагов» — шаги работы агента (contracts/ui.md §2). */
export const stepsText = (n: number) => plural(n, 'шаг', 'шага', 'шагов');

/** Тексты с подстановкой. */
export const say = {
  /** Свёрнутая работа агента: «Работал 2 мин · 8 шагов». */
  worked: (ms: number | null, steps: number) =>
    [ms === null ? 'Работал' : `Работал ${workedText(ms)}`, steps > 0 ? stepsText(steps) : null]
      .filter(Boolean)
      .join(' · '),
  /** Работу остановили кнопкой: «Остановлен · 3 шага». */
  stoppedTurn: (steps: number) =>
    ['Остановлен', steps > 0 ? stepsText(steps) : null].filter(Boolean).join(' · '),
  /** «Расход ответа: 11,8 тыс. токенов». */
  tokens: (n: number) =>
    n >= 1000
      ? `Расход ответа: ${(Math.round(n / 100) / 10).toFixed(1).replace('.', ',')} тыс. токенов`
      : `Расход ответа: ${plural(n, 'токен', 'токена', 'токенов')}`,
  /** Долгий расчёт сервера: «(расчёт 41)». */
  calc: (job: string) => `(расчёт ${job})`,
  /** Шапка панели в режиме проекта и окно проекта на странице «Помощник». */
  project: (planId: number) => `Проект новой версии правил №${planId}`,
  projectShort: (planId: number) => `Проект №${planId}`,
  /** Полоса «компьютер не в сети». */
  offline: (name: string, since: string | null) =>
    since ? `Компьютер ${name} не в сети с ${since}.` : `Компьютер ${name} не в сети.`,
  computerDisabled: (name: string) => `Компьютер ${name} отключён от табеля.`,
  loginExpired: (agent: string) => `Вход в ${agent} истёк.`,
  loggedOut: (agent: string) => `Вход в ${agent} не выполнен.`,
  notLinked: (agent: string) => `${agent} не вошёл в табель.`,
  notReady: (agent: string, state: string) => `${agent} не готов: ${state.toLowerCase()}.`,
  check: (agent: string) => `Проверить ${agent}`,
  /** Повтор после лимита: время по объекту, попытка — если известна. */
  retryAt: (time: string | null, attempt: number | null) =>
    time === null
      ? 'Агент повторит, когда лимит обновится.'
      : attempt === null
        ? `Агент повторит в ${time}.`
        : `Агент повторит в ${time}, попытка ${attempt} из 5.`,
  memoryFull: (percent: number) => `Память разговора заполнена на ${percent} %.`,
  /** Меню агента: компьютер и связь. */
  computerLine: (name: string, online: boolean) =>
    `Компьютер: ${name}, ${online ? LABEL.online : LABEL.offline}.`,
  /** Список «Мои разговоры»: «Claude · работает», «Claude · сегодня 13:40». */
  threadLine: (agent: string, when: string) => `${agent} · ${when}`,
  today: (time: string) => `сегодня ${time}`,
  yesterday: (time: string) => `вчера ${time}`,
  /** Шапка разговора на странице: «Claude · Sonnet 5.5». */
  threadMeta: (agent: string, model: string | null) => (model ? `${agent} · ${model}` : agent),
  archiveCount: (n: number) => `${TITLE.archive} · ${threadsText(n)}`,
  pageIntro: (computer: string | null) =>
    computer
      ? `Разговоры с ИИ-агентом о правилах расчёта. Агент работает на вашем компьютере ${computer}.`
      : 'Разговоры с ИИ-агентом о правилах расчёта.',
  computerChip: (name: string, online: boolean) => `${name} · ${online ? LABEL.online : LABEL.offline}`,
  /** «Спросить агента» на правиле: текст в поле сообщения. */
  askRule: (question: string) => `Объясни правило «${question.replace(/[?.!\s]+$/u, '')}» простыми словами`,
  /** Название кнопки разговора в списке для чтения с экрана. */
  openThread: (title: string) => `Открыть разговор «${title}»`,
};
